import { z } from 'zod';
const zPct = (min, max) => z.coerce
    .number()
    .min(min, { message: `${min}以上` })
    .max(max, { message: `${max}以下` });
const zPx = (min, max) => z.coerce
    .number()
    .int({ message: '整数' })
    .min(min, { message: `${min}px以上` })
    .max(max, { message: `${max}px以下` });
const zOpacity = () => z.coerce
    .number()
    .min(0, { message: '0以上' })
    .max(1, { message: '1以下' });
const zHexColor = () => z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/, { message: '例: #AABBCC' });
const zMs = (min, max) => z.coerce
    .number()
    .int({ message: '整数(ms)' })
    .min(min, { message: `${min}ms以上` })
    .max(max, { message: `${max}ms以下` });
export const LayoutTemplateId = z.enum(['dialog_overlay', 'split', 'fullscreen', 'evidence_board', 'cutin']);
const FitMode = z.enum(['cover', 'contain']);
const AxisY = z.enum(['top', 'center', 'bottom']);
const AxisX = z.enum(['left', 'center', 'right']);
const SplitDirection = z.enum(['vertical', 'horizontal']);
const UiDensity = z.enum(['none', 'minimal', 'standard']);
const PortraitSize = z.enum(['S', 'M', 'L']);
const CutinMotion = z.enum(['fade', 'slide', 'zoom']);
const CutinBg = z.enum(['glass', 'solid', 'none']);
const CommonVisualParams = z
    .object({
    safeArea: z.boolean().default(true),
    outerPaddingPx: zPx(0, 32).default(12),
    bgFit: FitMode.default('cover'),
    bgDimOpacity: zOpacity().default(0.2),
    bgDimColor: zHexColor().default('#000000')
})
    .default({});
const DialogBoxParams = z
    .object({
    heightPct: zPct(22, 60).default(35),
    paddingPx: zPx(8, 32).default(16),
    radiusPx: zPx(0, 32).default(16),
    opacity: zOpacity().default(0.9),
    blur: z.boolean().default(true),
    showSpeakerName: z.boolean().default(true),
    showTapHint: z.boolean().default(true)
})
    .default({});
const PortraitParams = z
    .object({
    enabled: z.boolean().default(true),
    size: PortraitSize.default('M'),
    yAlign: AxisY.default('bottom'),
    yOffsetPct: zPct(-20, 20).default(0),
    xOffsetPct: zPct(-10, 10).default(0),
    dimWhenNotSpeaking: z.boolean().default(true)
})
    .default({});
const SpeakerFocusParams = z
    .object({
    // undefined = シナリオデフォルトに追従（overrideしない）
    enabled: z.boolean().optional(),
    zoomScale: z.coerce.number().min(1).max(1.6).default(1.15),
    transitionMs: zMs(0, 2000).default(180),
    inactiveOpacity: zOpacity().default(0.45)
})
    .default({});
const DialogOverlayParams = z
    .object({
    common: CommonVisualParams,
    dialogBox: DialogBoxParams,
    portraits: z
        .object({
        left: PortraitParams,
        right: PortraitParams
    })
        .default({}),
    speakerFocus: SpeakerFocusParams,
    logButton: z
        .object({
        enabled: z.boolean().default(false),
        position: z.enum(['top_right', 'top_left']).default('top_right')
    })
        .default({})
})
    .default({});
const SplitParams = z
    .object({
    common: CommonVisualParams,
    direction: SplitDirection.default('vertical'),
    ratioPct: zPct(30, 70).default(55),
    gapPx: zPx(0, 24).default(8),
    showDivider: z.boolean().default(true),
    dialogBox: DialogBoxParams,
    mediaCornerRadiusPx: zPx(0, 24).default(16)
})
    .default({});
const FullscreenParams = z
    .object({
    common: CommonVisualParams,
    uiDensity: UiDensity.default('minimal'),
    showSkip: z.boolean().default(false),
    showClose: z.boolean().default(false),
    hudOpacity: zOpacity().default(0.85)
})
    .default({});
const EvidenceBoardParams = z
    .object({
    common: CommonVisualParams,
    headerHeightPct: zPct(10, 25).default(15),
    footerHeightPct: zPct(10, 25).default(18),
    showHintButton: z.boolean().default(true),
    hintButtonPosition: z.enum(['header_right', 'header_left', 'footer_right']).default('header_right'),
    cardRadiusPx: zPx(0, 24).default(16),
    cardOpacity: zOpacity().default(0.85),
    accentColor: zHexColor().default('#A855F7')
})
    .default({});
const CutinParams = z
    .object({
    common: CommonVisualParams,
    positionY: AxisY.default('center'),
    alignX: AxisX.default('center'),
    widthPct: zPct(60, 100).default(85),
    bgStyle: CutinBg.default('glass'),
    bgOpacity: zOpacity().default(0.8),
    border: z.boolean().default(true),
    accentColor: zHexColor().default('#F59E0B'),
    durationMs: zMs(300, 8000).default(1500),
    motionIn: CutinMotion.default('slide'),
    motionOut: CutinMotion.default('fade')
})
    .default({});
const LayoutDialogOverlay = z.object({
    v: z.literal(1),
    templateId: z.literal('dialog_overlay'),
    params: DialogOverlayParams
});
const LayoutSplit = z.object({
    v: z.literal(1),
    templateId: z.literal('split'),
    params: SplitParams
});
const LayoutFullscreen = z.object({
    v: z.literal(1),
    templateId: z.literal('fullscreen'),
    params: FullscreenParams
});
const LayoutEvidenceBoard = z.object({
    v: z.literal(1),
    templateId: z.literal('evidence_board'),
    params: EvidenceBoardParams
});
const LayoutCutin = z.object({
    v: z.literal(1),
    templateId: z.literal('cutin'),
    params: CutinParams
});
export const LayoutSchemaV1 = z.discriminatedUnion('templateId', [
    LayoutDialogOverlay,
    LayoutSplit,
    LayoutFullscreen,
    LayoutEvidenceBoard,
    LayoutCutin
]);
export function defaultLayout(templateId) {
    return LayoutSchemaV1.parse({ v: 1, templateId, params: {} });
}
//# sourceMappingURL=layoutSchema.js.map